import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Heading,
    Input,
    Button,
    Switch,
    FormControl,
    FormLabel,
    useToast,
    VStack,
    HStack,
    Divider,
    Select, 
} from '@chakra-ui/react';
import { FaSave } from 'react-icons/fa';
import axios from 'axios';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

const UpdateInternshipDetails = () => {
    const { isAdmin } = useSelector((state) => state.user);
    const { id } = useParams();
    const internshipId = parseInt(id, 10);
    const toast = useToast();
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [completionCertificate, setCompletionCertificate] = useState(false);
    const [duration, setDuration] = useState('');
    const [fees, setFees] = useState('');
    const [Internship_type, setInternshipType] = useState(''); 
    const [keySkills, setKeySkills] = useState('');
    const [LOR, setLOR] = useState(false);
    const [paperWriting, setPaperWriting] = useState(false);
    const [projectCount, setProjectCount] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [eligibility, setEligibility] = useState('');
    const [skillLevel, setSkillLevel] = useState(''); 

    const getInternshipDetails = async () => {
        setIsLoading(true);
        setError('');
        try {
            const accessToken = window.sessionStorage.getItem('accessToken');
            const { data } = await axios.get(`https://gyanbackend.aim4u.co.in/super/view-internships/${internshipId}/`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setTitle(data.Internship_title);
            setCompletionCertificate(data.completion_certificate);
            setDuration(data.duration_weeks);
            setFees(data.fees);
            setInternshipType(data.Internship_type || ''); 
            setKeySkills(data.key_skills);
            setLOR(data.lor);
            setPaperWriting(data.paper_writing);
            setProjectCount(data.project_count);
            setEligibility(data.eligibility_criteria || '');
            setSkillLevel(data.skill_level || '');
            setIsLoading(false);
        } catch (err) {
            console.error('Error fetching internship details:', err);
            setError('Failed to fetch internship details.');
            setIsLoading(false);
            toast({
                title: 'Fetch Error',
                description: 'Could not retrieve internship information.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const updateDetails = async () => {
        try {
            const accessToken = window.sessionStorage.getItem('accessToken');
            await axios.patch(
                `https://gyanbackend.aim4u.co.in/super/view-internships/${internshipId}/`,
                {
                    Internship_title: title,
                    Internship_type: Internship_type, 
                    key_skills: keySkills,
                    duration_weeks: duration,
                    project_count: projectCount,
                    paper_writing: paperWriting,
                    lor: LOR,
                    completion_certificate: completionCertificate,
                    fees,
                    eligibility_criteria: eligibility,
                    skill_level: skillLevel,
                },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            toast({
                title: 'Update Successful',
                description: 'Internship details updated successfully.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            navigate('/internship');
        } catch (error) {
            toast({
                title: 'Update Failed',
                description: 'Something went wrong while updating.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            console.error('Error updating internship:', error);
        }
    };

    useEffect(() => {
        getInternshipDetails();
    }, []);

    if (isLoading) {
        return (
            <>
                <NavBar isAdmin={isAdmin} />
                <Box minH="100vh" py={10} display="flex" justifyContent="center" alignItems="center">
                    <Heading as="h2" size="md">Loading internship details...</Heading>
                </Box>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <NavBar isAdmin={isAdmin} />
                <Box minH="100vh" py={10} display="flex" justifyContent="center" alignItems="center">
                    <Heading as="h2" size="md" color="red.500">{error}</Heading>
                </Box>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavBar isAdmin={isAdmin} />
            <Box bg="#e8f3fd" minH="100vh" py={10} display="flex" flexDirection="column" alignItems="center">
                <Box
                    bg="white"
                    p={8}
                    borderRadius={8}
                    boxShadow="md"
                    w={['95%', '80%', '60%']}
                    maxWidth="800px"
                >
                    <Heading as="h1" size="xl" mb={6} textAlign="center" color="teal.500">
                        Edit Internship Details
                    </Heading>
                    <Divider mb={6} />
                    <VStack spacing={4}>
                        <FormControl id="title">
                            <FormLabel fontWeight="semibold">Title</FormLabel>
                            <Input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter internship title"
                            />
                        </FormControl>

                        <FormControl id="eligibility">
                            <FormLabel fontWeight="semibold">Eligibility Criteria</FormLabel>
                            <Input
                                type="text"
                                value={eligibility}
                                onChange={(e) => setEligibility(e.target.value)}
                                placeholder="e.g., B.Tech/BE Computer Science"
                            />
                        </FormControl>

                        <FormControl id="Internship_type">
                            <FormLabel fontWeight="semibold">Internship Type</FormLabel>
                            <Select value={Internship_type} onChange={(e) => setInternshipType(e.target.value)}>
                                <option value="">Select Type</option>
                                <option value="Remote">Remote</option>
                                <option value="On-Site">On-Site</option>
                                <option value="Hybrid">Hybrid</option>
                            </Select>
                        </FormControl>

                        <FormControl id="keySkills">
                            <FormLabel fontWeight="semibold">Key Skills</FormLabel>
                            <Input
                                type="text"
                                value={keySkills}
                                onChange={(e) => setKeySkills(e.target.value)}
                                placeholder="List required key skills (comma-separated)"
                            />
                        </FormControl>

                        <HStack w="full">
                            <FormControl id="duration">
                                <FormLabel fontWeight="semibold">Duration (weeks)</FormLabel>
                                <Input
                                    type="number"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    placeholder="Enter duration in weeks"
                                />
                            </FormControl>

                            <FormControl id="fees">
                                <FormLabel fontWeight="semibold">Fees</FormLabel>
                                <Input
                                    type="number"
                                    value={fees}
                                    onChange={(e) => setFees(e.target.value)}
                                    placeholder="Enter fees (if applicable)"
                                />
                            </FormControl>
                        </HStack>

                        <HStack w="full" justify="space-between">
                            <FormControl display="flex" alignItems="center">
                                <FormLabel htmlFor="completionCertificate" mb="0" fontWeight="semibold">
                                    Completion Certificate
                                </FormLabel>
                                <Switch
                                    id="completionCertificate"
                                    isChecked={completionCertificate}
                                    onChange={() => setCompletionCertificate(!completionCertificate)}
                                    colorScheme="teal"
                                />
                            </FormControl>

                            <FormControl display="flex" alignItems="center">
                                <FormLabel htmlFor="lor" mb="0" fontWeight="semibold">
                                    Letter of Recommendation (LOR)
                                </FormLabel>
                                <Switch id="lor" isChecked={LOR} onChange={() => setLOR(!LOR)} colorScheme="teal" />
                            </FormControl>
                        </HStack>

                        <HStack w="full" justify="space-between">
                            <FormControl display="flex" alignItems="center">
                                <FormLabel htmlFor="paperWriting" mb="0" fontWeight="semibold">
                                    Paper Writing Opportunity
                                </FormLabel>
                                <Switch
                                    id="paperWriting"
                                    isChecked={paperWriting}
                                    onChange={() => setPaperWriting(!paperWriting)}
                                    colorScheme="teal"
                                />
                            </FormControl>

                            <FormControl id="projectCount">
                                <FormLabel fontWeight="semibold">Project Count</FormLabel>
                                <Input
                                    type="number"
                                    value={projectCount}
                                    onChange={(e) => setProjectCount(e.target.value)}
                                    placeholder="Number of projects"
                                />
                            </FormControl>
                        </HStack>

                        <Button
                            leftIcon={<FaSave />}
                            colorScheme="teal"
                            w="full"
                            mt={6}
                            onClick={updateDetails}
                            fontWeight="semibold"
                        >
                            Save Changes
                        </Button>
                    </VStack>
                </Box>
            </Box>
            <Footer />
        </>
    );
};

export default UpdateInternshipDetails;